import { LoadingOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, Grid, Input, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Send from "../../../../assets/images/icons/Send";
import { CustomMessage } from "../../../../components";
import { P1 } from "../../../../components/NormalFonts";
import { queryKey } from "../../../../enumerations/queryKey";
import ChatBubble from "../ChatBubble";
import { ChatRoomProps } from "./data";
import { useGetChatContent, useSendMessage } from "./hooks/chat.hooks";
import { ChatItemType, ChatResponse, SendChatParams } from "./hooks/data";
import styles from "./styles.module.scss";
import "./styles.scss";

const ChatRoom: React.FC<ChatRoomProps> = ({ transactionID }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: chatContent } = useGetChatContent(
    { transId: transactionID || "" },
    { enabled: !!transactionID }
  );
  const { mutateAsync: sendMessage, isPending: isPendingSendMessage } =
    useSendMessage();
  const [message, setMessage] = useState<string>("");
  const [isComposing, setIsComposing] = useState(false);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const handleChangeMessage = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const handleSendMessage = async () => {
    if (!message) return;
    setMessage("");

    const request: SendChatParams = {
      order_number: transactionID || "",
      message: message,
    };
    try {
      await sendMessage(
        { params: request },
        {
          onSuccess: (data) => {
            console.debug("message sent", data);
            queryClient.invalidateQueries({
              queryKey: [queryKey.CHAT_LIST, transactionID],
            });
          },
          onError: (error) => {
            console.debug("error", error);
          },
        }
      );
    } catch (error: any) {
      CustomMessage({
        alertMessage: t([
          `error.${error.data.result.code}`,
          error.data.result.message,
        ]),
        alertType: "error",
      });
    }
  };

  const scrollToBottom = () => {
    const el = containerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight; // ✅ only scrolls this container
    }
  };

  // const scrollChatRoomToBottom = () => {
  //   setTimeout(() => {
  //     const mainLayout = document.getElementById("main-layout");
  //     const chatRoom = document
  //       .querySelector(`.${styles.chatContainer}`)
  //       ?.closest(".ant-card");
  //     if (mainLayout && chatRoom) {
  //       const chatRoomRect = chatRoom.getBoundingClientRect();
  //       const mainLayoutRect = mainLayout.getBoundingClientRect();
  //       const scrollDistance = chatRoomRect.bottom - mainLayoutRect.bottom;
  //       mainLayout.scrollTo({
  //         top: mainLayout.scrollTop + scrollDistance,
  //         behavior: "smooth",
  //       });
  //     }
  //   }, 500);
  // };

  useEffect(() => {
    if (chatContent?.length <= 1) return;
    scrollToBottom();
    console.debug("chatContent", chatContent);
  }, [chatContent]);

  const renderChatBubbles = (chatContent: ChatResponse) => {
    return (
      <>
        {chatContent?.map((chat: ChatItemType, index: number) => {
          return <ChatBubble chat={chat} key={`chat_${index}`} />;
        })}
      </>
    );
  };

  return (
    <Card
      style={{ width: "100%", marginTop: 32 }}
      styles={{
        header: { background: "var(--primary-10)" },
        body: { padding: 0, overflow: "hidden" },
      }}
      size={screens.sm ? "default" : "small"}
      title={
        <P1 weight="medium" color=" var(--text-content-1)">
          {t("chatroom.title")}
        </P1>
      }
      variant="borderless"
    >
      <div className={styles.chatContainer}>
        <div className={styles.chatBody} ref={containerRef}>
          {renderChatBubbles(chatContent)}
          <Spin
            indicator={<LoadingOutlined spin />}
            spinning={isPendingSendMessage}
            className={styles.loading}
          />
        </div>
        <div className={styles.chatInput}>
          <Input.TextArea
            value={message}
            onChange={handleChangeMessage}
            className="chat-input"
            variant={"filled"}
            placeholder={t("chatroom.message.hint", { id: transactionID })}
            autoSize={{ minRows: 1, maxRows: 3 }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !isComposing) {
                e.preventDefault();
                if (message) {
                  handleSendMessage();
                }
              }
            }}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            // onFocus={() => scrollChatRoomToBottom()}
            // autoFocus={true}
          />
          <Button
            onClick={handleSendMessage}
            size="small"
            type="link"
            disabled={!message}
            loading={isPendingSendMessage}
            icon={<Send style={{ color: "var(--primary)", fontSize: 24 }} />}
          />
        </div>
      </div>
    </Card>
  );
};

export default ChatRoom;
